import { ModelSwitcher } from "@/components/Chat/header/ModelSwitcher";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import ColorIconChooser from "@/components/ui/color-icon-chooser";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useSidebar } from "@/components/ui/sidebar";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { WorkspaceTopbar } from "@/components/workspace";
import EditProjectDialog from "@/components/workspace/dialogs/EditProjectDialog";
import ManageTeamDialog from "@/components/workspace/dialogs/ManageTeamDialog";
import WorkspaceCreateDialog from "@/components/workspace/dialogs/WorkspaceCreateDialog";
import WorkspaceSettingsDialog from "@/components/workspace/dialogs/WorkspaceSettingsDialog";
import WorkspaceFiles from "@/components/workspace/WorkspaceFiles";
import { MemoryManagementDialog } from "@/components/Settings/MemoryManagementDialog";
import { ProjectDeleteDialog } from "@/features/sidebar/projects/dialogs/ProjectDeleteDialog";
import { ProjectRenameDialog } from "@/features/sidebar/projects/dialogs/ProjectRenameDialog";
import { isPersonalProject } from "@/features/sidebar/projects/utils";
import { supabase } from "@/lib";
import { SupabaseWorkspaceUtils } from "@/lib/db/utils/supabase/workspace";
import { cn } from "@/lib/utils";
import { dropdownMenuItemDestructiveClasses } from "@/lib/constants/menu-styles";
import { updateUserWorkspaces } from "@/lib/utils/notificationUtils";
import useAuthStore from "@/store/authStore";
import {
  Bell,
  MoreHorizontal,
  Palette,
  PanelLeft,
  Pencil,
  Plus,
  Trash2,
  Users,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

export function ProjectsNavBar() {
  const params = useParams();
  const { workspaces, user, setWorkspaces } = useAuthStore();
  const [isWorkspaceCreateOpen, setIsWorkspaceCreateOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isFilesOpen, setIsFilesOpen] = useState(false);
  const [isMemoriesOpen, setIsMemoriesOpen] = useState(false);
  const [isEditProjectOpen, setIsEditProjectOpen] = useState(false);
  const [isManageTeamOpen, setIsManageTeamOpen] = useState(false);
  const [manageTeamInitialTab, setManageTeamInitialTab] = useState("invitations");
  const [teamMembers, setTeamMembers] = useState([]);
  const [isColorChooserOpen, setIsColorChooserOpen] = useState(false);
  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [newWorkspaceName, setNewWorkspaceName] = useState("");
  const [isRenamingWorkspace, setIsRenamingWorkspace] = useState(false);
  const [isDeletingWorkspace, setIsDeletingWorkspace] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [sidebarTooltipOpen, setSidebarTooltipOpen] = useState(false);
  const { state: sidebarState, toggleSidebar } = useSidebar();
  const navigate = useNavigate();

  // Platform detection for keyboard shortcuts
  const isMac =
    typeof window !== "undefined" &&
    navigator.platform.toUpperCase().indexOf("MAC") >= 0;
  const modifierKey = isMac ? "⌘" : "Ctrl";

  // Handle responsive layout
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // State for notifications
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  // Load team members for workspace
  useEffect(() => {
    const loadTeamMembers = async () => {
      if (!params.workspaceId) return;
      const workspace = workspaces.find((w) => w.id === params.workspaceId);
      if (!workspace?.is_team) {
        setTeamMembers([]);
        return;
      }

      try {
        // First get workspace members
        const { data: members, error: membersError } = await supabase
          .from("workspace_members")
          .select("user_id, role")
          .eq("workspace_id", params.workspaceId);

        if (membersError || !members || members.length === 0) {
          setTeamMembers([]);
          return;
        }

        // Then get profiles for those users
        const userIds = members.map((m) => m.user_id);
        const { data: profiles, error: profilesError } = await supabase
          .from("profiles")
          .select("id, full_name, email, avatar_url")
          .in("id", userIds);

        if (profilesError) {
          console.error("Error loading profiles:", profilesError);
          setTeamMembers([]);
          return;
        }

        // Combine members with profiles
        const profileMap = new Map(profiles?.map((p) => [p.id, p]) || []);
        const formattedMembers = members.map((m) => {
          const profile = profileMap.get(m.user_id);
          return {
            id: m.user_id,
            role: m.role,
            full_name: profile?.full_name || profile?.email?.split("@")[0] || "User",
            email: profile?.email,
            avatar_url: profile?.avatar_url,
          };
        });
        setTeamMembers(formattedMembers);
      } catch (err) {
        console.error("Error loading team members:", err);
      }
    };

    loadTeamMembers();
  }, [params.workspaceId, workspaces]);

  // Helper to get initials
  const getInitials = (name) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  // Determine the type of workspace page and find current workspace
  let type = "workspaces";
  let workspace = null;

  if (params.workspaceId) {
    workspace = workspaces.find((w) => w.id === params.workspaceId);
    type = "workspace";
  }

  // Handler for opening rename dialog
  const handleOpenRenameDialog = () => {
    setNewWorkspaceName(workspace?.name || "");
    setIsRenameDialogOpen(true);
  };

  // Handler for renaming workspace
  const handleWorkspaceRename = async () => {
    if (!newWorkspaceName.trim() || !workspace?.id) return;
    setIsRenamingWorkspace(true);
    try {
      const { error } = await supabase
        .from("workspaces")
        .update({ name: newWorkspaceName.trim() })
        .eq("id", workspace.id);
      if (!error) {
        setWorkspaces(workspaces.map(w =>
          w.id === workspace.id ? { ...w, name: newWorkspaceName.trim() } : w
        ));
        setIsRenameDialogOpen(false);
      }
    } catch (err) {
      console.error("Error renaming workspace:", err);
    } finally {
      setIsRenamingWorkspace(false);
    }
  };

  // Handler for deleting workspace
  const handleWorkspaceDelete = async () => {
    if (!workspace?.id) return;
    setIsDeletingWorkspace(true);
    try {
      const { error } = await supabase
        .from("workspaces")
        .delete()
        .eq("id", workspace.id);
      if (!error) {
        setWorkspaces(workspaces.filter(w => w.id !== workspace.id));
        setIsDeleteDialogOpen(false);
        navigate("/projects");
      }
    } catch (err) {
      console.error("Error deleting workspace:", err);
    } finally {
      setIsDeletingWorkspace(false);
    }
  };

  // Handler for workspace customization (color/icon)
  const handleWorkspaceCustomize = async (updates) => {
    if (!workspace?.id) return;
    try {
      const { error } = await supabase
        .from("workspaces")
        .update({
          metadata: {
            ...(workspace.metadata || {}),
            ...updates,
          },
        })
        .eq("id", workspace.id);
      if (!error) {
        setWorkspaces(workspaces.map(w =>
          w.id === workspace.id
            ? { ...w, metadata: { ...(w.metadata || {}), ...updates } }
            : w
        ));
      }
    } catch (err) {
      console.error("Error updating workspace:", err);
    }
  };

  // Define action buttons based on page type
  let actionButtons = null;

  if (type === "workspaces") {
    // Workspaces listing page
    actionButtons = (
      <div
        className={cn(
          "flex items-center gap-2",
          isMobile && "w-full justify-end",
          sidebarState === "collapsed" ? "pr-1" : "pr-2",
          "transition-all duration-300"
        )}
      >
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size={isMobile ? "sm" : "sm"}
                className={cn(
                  "bg-white/70 dark:bg-zinc-900/50 backdrop-blur-sm",
                  "border-purple-200 dark:border-purple-800/40 text-purple-700 dark:text-purple-300",
                  "hover:bg-purple-50 dark:hover:bg-purple-900/30 rounded-full shadow-sm hover:shadow",
                  "transition-all duration-200 relative",
                  isMobile && "h-9 px-2"
                )}
                onClick={() => setIsNotificationsOpen(true)}
              >
                <Bell className={cn("h-4 w-4")} />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Notifications</p>
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size={isMobile ? "sm" : "sm"}
                className={cn(
                  "bg-white/70 dark:bg-zinc-900/50 backdrop-blur-sm",
                  "border-purple-200 dark:border-purple-800/40 text-purple-700 dark:text-purple-300",
                  "hover:bg-purple-50 dark:hover:bg-purple-900/30 rounded-full shadow-sm hover:shadow",
                  "transition-all duration-200",
                  isMobile && "h-9 px-2"
                )}
                onClick={() => setIsWorkspaceCreateOpen(true)}
              >
                <Plus className={cn("h-4 w-4")} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Create Workspace</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    );
  } else if (type === "workspace") {
    // Single workspace page - team avatars + 3-dot menu in navbar
    actionButtons = (
      <div
        className={cn(
          "flex items-center gap-3",
          isMobile ? "w-full justify-end mt-1" : "ml-auto",
          sidebarState === "collapsed" ? "pr-1" : "pr-2",
          "transition-all duration-300"
        )}
      >
        {/* Team member avatars (for team workspaces) */}
        {workspace?.is_team && teamMembers.length > 0 && (
          <TooltipProvider>
            <div className="flex items-center">
              {/* Quick invite button */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => {
                      setManageTeamInitialTab("invitations");
                      setTimeout(() => setIsManageTeamOpen(true), 0);
                    }}
                    className="w-8 h-8 rounded-full bg-zinc-100 dark:bg-zinc-800 border-2 border-white dark:border-zinc-900 flex items-center justify-center text-zinc-500 dark:text-zinc-400 cursor-pointer hover:ring-2 hover:ring-purple-500 hover:bg-purple-100 dark:hover:bg-purple-900/30 hover:text-purple-600 dark:hover:text-purple-400 transition-all mr-1"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Invite team members</p>
                </TooltipContent>
              </Tooltip>
              <div
                className="flex -space-x-2 cursor-pointer"
                onClick={() => {
                  setManageTeamInitialTab("members");
                  setIsManageTeamOpen(true);
                }}
              >
                {teamMembers.slice(0, 5).map((member) => (
                  <Tooltip key={member.id}>
                    <TooltipTrigger asChild>
                      <Avatar className="w-8 h-8 border-2 border-white dark:border-zinc-900 hover:ring-2 hover:ring-purple-500 transition-all">
                        {member.avatar_url ? (
                          <AvatarImage src={member.avatar_url} alt={member.full_name} />
                        ) : null}
                        <AvatarFallback
                          className="text-xs font-medium"
                          style={{
                            backgroundColor: `hsl(${(member.id?.charCodeAt(0) || 0) * 10}, 70%, 60%)`,
                            color: "white",
                          }}
                        >
                          {getInitials(member.full_name)}
                        </AvatarFallback>
                      </Avatar>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{member.full_name}</p>
                    </TooltipContent>
                  </Tooltip>
                ))}
                {teamMembers.length > 5 && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="w-8 h-8 rounded-full bg-zinc-200 dark:bg-zinc-700 border-2 border-white dark:border-zinc-900 flex items-center justify-center text-xs font-medium text-zinc-600 dark:text-zinc-300 cursor-pointer hover:ring-2 hover:ring-purple-500 transition-all">
                        +{teamMembers.length - 5}
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{teamMembers.length - 5} more members</p>
                    </TooltipContent>
                  </Tooltip>
                )}
              </div>
            </div>
          </TooltipProvider>
        )}

        {/* ColorIconChooser - moved outside dropdown to prevent popover from closing */}
        <ColorIconChooser
          isOpen={isColorChooserOpen}
          onOpenChange={setIsColorChooserOpen}
          currentColor={workspace?.metadata?.color || "#9333ea"}
          currentIcon={workspace?.metadata?.icon || "folder"}
          onColorSelect={(color) => handleWorkspaceCustomize({ color: color.value })}
          onIconSelect={(icon) => handleWorkspaceCustomize({ icon: icon.value })}
        >
          <span className="sr-only">Customize project</span>
        </ColorIconChooser>

        {/* 3-dot dropdown menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-[160px] rounded-2xl bg-background/80 backdrop-blur-md border border-border/50">
            <DropdownMenuItem
              className="flex cursor-pointer items-center rounded-xl"
              onClick={() => {
                // Small delay to let the dropdown close first
                setTimeout(() => setIsColorChooserOpen(true), 100);
              }}
            >
              <Palette className="mr-2 h-4 w-4" />
              Customize
            </DropdownMenuItem>
            {/* Manage Team - only for team projects */}
            {workspace?.is_team && (
              <DropdownMenuItem
                className="flex cursor-pointer items-center rounded-xl"
                onClick={() => {
                  setTimeout(() => {
                    setManageTeamInitialTab("members");
                    setIsManageTeamOpen(true);
                  }, 0);
                }}
              >
                <Users className="mr-2 h-4 w-4" />
                Manage Team
              </DropdownMenuItem>
            )}
            {/* Hide Rename and Delete for Personal Project */}
            {!isPersonalProject(workspace) && (
              <>
                <DropdownMenuItem
                  className="flex cursor-pointer items-center rounded-xl"
                  onClick={() => setTimeout(handleOpenRenameDialog, 0)}
                >
                  <Pencil className="mr-2 h-4 w-4" />
                  Rename
                </DropdownMenuItem>
                <DropdownMenuSeparator className="my-1" />
                <DropdownMenuItem
                  className={cn(
                    "flex cursor-pointer items-center rounded-xl",
                    dropdownMenuItemDestructiveClasses
                  )}
                  onClick={() => setTimeout(() => setIsDeleteDialogOpen(true), 0)}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  }

  // Render dialogs for workspace specific functionality
  return (
    <>
      <div
        className={cn(
          "w-full flex items-center",
          isMobile ? "flex-col items-start gap-2 py-1" : "justify-between",
          sidebarState === "collapsed" ? "pr-2" : "pr-3",
          "transition-all duration-300"
        )}
      >
        {type === "workspace" ? (
          // For individual workspace pages, render custom layout with ModelSwitcher
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-3">
              {/* Mobile sidebar toggle - only visible on narrow screens */}
              {isMobile && (
                <TooltipProvider>
                  <Tooltip
                    open={sidebarTooltipOpen}
                    onOpenChange={setSidebarTooltipOpen}
                  >
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 rounded-full flex-shrink-0"
                        onClick={() => {
                          setSidebarTooltipOpen(false);
                          toggleSidebar();
                        }}
                        aria-label="Toggle sidebar"
                      >
                        <PanelLeft className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">
                      <p>Toggle Sidebar ({modifierKey}B)</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              <ModelSwitcher zIndex={300} />
            </div>
            {actionButtons && (
              <div className="flex items-center gap-3 flex-shrink-0">
                {actionButtons}
              </div>
            )}
          </div>
        ) : (
          // For workspaces listing page, use the original WorkspaceTopbar
          <div className="flex items-center w-full gap-3">
            {/* Mobile sidebar toggle - only visible on narrow screens */}
            {isMobile && (
              <TooltipProvider>
                <Tooltip
                  open={sidebarTooltipOpen}
                  onOpenChange={setSidebarTooltipOpen}
                >
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 rounded-full flex-shrink-0"
                      onClick={() => {
                        setSidebarTooltipOpen(false);
                        toggleSidebar();
                      }}
                      aria-label="Toggle sidebar"
                    >
                      <PanelLeft className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    <p>Toggle Sidebar ({modifierKey}B)</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            <WorkspaceTopbar
              type={type}
              workspace={workspace}
              actionButtons={actionButtons}
              onBackClick={() => {
                // For the workspaces page, always go back to chat
                if (type === "workspaces") {
                  navigate("/chat");
                } else if (type === "workspace") {
                  // From workspace page, go to new chat
                  navigate("/chat/new");
                }
              }}
              className={cn(
                "w-full",
                isMobile && "mb-2",
                sidebarState === "collapsed" ? "pl-0" : "pl-1",
                isMobile ? "pl-2" : "",
                "transition-all duration-300"
              )}
              backButtonOnly={false}
            />
          </div>
        )}
      </div>

      {/* Notifications Dialog */}
      <Dialog
        open={isNotificationsOpen}
        onOpenChange={setIsNotificationsOpen}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Notifications</DialogTitle>
            <DialogDescription>
              View your recent notifications and updates
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-[60vh] overflow-y-auto py-4">
            {notifications && notifications.length > 0 ? (
              <div className="space-y-4">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className="flex items-start p-3 rounded-lg border border-gray-100 dark:border-gray-800 bg-white dark:bg-zinc-900"
                  >
                    <div className="flex-1">
                      <p className="text-sm font-medium">
                        {notification.title}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {notification.message}
                      </p>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                        {new Date(notification.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Bell className="mx-auto h-12 w-12 text-gray-300 dark:text-gray-600 mb-2" />
                <p className="text-gray-500 dark:text-gray-400">
                  No notifications yet
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsNotificationsOpen(false)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Workspace creation dialog */}
      {type === "workspaces" && (
        <WorkspaceCreateDialog
          isOpen={isWorkspaceCreateOpen}
          onOpenChange={setIsWorkspaceCreateOpen}
          onCreateWorkspace={async (data) => {
            try {
              // Create the workspace using authStore method
              const createdWorkspace = await useAuthStore.getState().createWorkspace({
                name: data.name,
                description: data.description,
                instructions: data.instructions,
                is_personal: data.is_personal,
                is_team: data.is_team,
                team_key: data.team_key,
                allow_invites: data.allow_invites,
                image_path: data.image_path,
                created_now: data.created_now,
                metadata: data.metadata,
                settings: {
                  notifications: false,
                  private: data.is_team,
                  allowMemberInvites: data.is_team && data.allow_invites,
                },
              });

              // Create a vector store for the workspace
              if (createdWorkspace && createdWorkspace.id) {
                try {
                  const response = await fetch("/api/vector-stores/create", {
                    method: "POST",
                    headers: {
                      "Content-Type": "application/json",
                    },
                    credentials: "include",
                    body: JSON.stringify({
                      name: createdWorkspace.name,
                      workspace_id: createdWorkspace.id,
                      user_id: user?.id,
                      metadata: {
                        created_by: user?.id,
                        workspace_name: createdWorkspace.name,
                        is_team: data.is_team,
                        created_at: new Date().toISOString(),
                      },
                    }),
                  });

                  if (response.ok) {
                    const vectorStoreData = await response.json();

                    await supabase
                      .from("workspaces")
                      .update({
                        vector_store_id: vectorStoreData.data.id,
                        metadata: {
                          ...(createdWorkspace.metadata || {}),
                          vector_store_id: vectorStoreData.data.id,
                          vector_store_created_at: new Date().toISOString(),
                          created_now: createdWorkspace.created_now,
                          workspace_type: createdWorkspace.is_team
                            ? "team"
                            : "personal",
                        },
                      })
                      .eq("id", createdWorkspace.id);
                  } else {
                    console.error(
                      "Error creating vector store:",
                      await response.text()
                    );
                  }
                } catch (vectorStoreError) {
                  console.error(
                    "Error creating vector store:",
                    vectorStoreError
                  );
                }
              }

              // Update the user's profile with the new workspace ID and add to store
              if (createdWorkspace && createdWorkspace.id && user?.id) {
                try {
                  await updateUserWorkspaces(user.id, createdWorkspace.id);

                  setWorkspaces([...workspaces, createdWorkspace]);

                  setTimeout(() => {
                    setWorkspaces((currentWorkspaces) => {
                      const updatedWorkspaces = currentWorkspaces.map((w) =>
                        w.id === createdWorkspace.id ? { ...w, ...createdWorkspace } : w
                      );
                      return updatedWorkspaces;
                    });
                  }, 100);

                  await useAuthStore.getState().refreshUserProfile();
                } catch (profileError) {
                  console.error(
                    "Error updating user's profile with new workspace:",
                    profileError
                  );
                  setWorkspaces([...workspaces, createdWorkspace]);
                }
              } else {
                setWorkspaces([...workspaces, createdWorkspace]);
              }

              setIsWorkspaceCreateOpen(false);

              if (data.is_team) {
                navigate(`/projects/${createdWorkspace.id}`);
              }
            } catch (error) {
              console.error("Error creating workspace:", error);
            }
          }}
        />
      )}

      {/* Workspace specific dialogs */}
      {type === "workspace" && workspace && (
        <>
          {/* Files Dialog */}
          <Dialog open={isFilesOpen} onOpenChange={setIsFilesOpen}>
            <DialogContent
              className="max-w-3xl rounded-2xl bg-white dark:bg-zinc-900 border-gray-100 dark:border-zinc-800 shadow-xl"
              style={{ zIndex: 9999 }}
              forceMount
              hideCloseButton={true}
            >
              <Button
                variant="outline"
                size="icon"
                onClick={() => setIsFilesOpen(false)}
                className="absolute right-4 top-4 h-8 w-8 rounded-full text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white border-zinc-300 dark:border-zinc-700 hover:bg-zinc-100 dark:hover:bg-zinc-800 z-[450]"
              >
                <X className="h-5 w-5" />
              </Button>

              <DialogHeader className="pb-3">
                <DialogTitle className="text-xl font-bold text-gray-900 dark:text-white tracking-tight">
                  Project Files
                </DialogTitle>
                <DialogDescription className="text-gray-500 dark:text-zinc-400 mt-2">
                  Manage files associated with this project
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 overflow-y-auto max-h-[60vh]">
                <WorkspaceFiles
                  workspaceId={params.workspaceId}
                  vectorStoreId={workspace?.vector_store_id}
                  onUpload={async () => {}}
                />
              </div>
            </DialogContent>
          </Dialog>

          {/* Memories Dialog */}
          <MemoryManagementDialog
            open={isMemoriesOpen}
            onOpenChange={setIsMemoriesOpen}
            workspaceId={params.workspaceId}
          />

          {/* Settings Dialog */}
          <WorkspaceSettingsDialog
            isOpen={isSettingsOpen}
            onOpenChange={setIsSettingsOpen}
            workspace={workspace}
            onSaveChanges={async (updatedWorkspaceData) => {
              try {
                const updatedWorkspace =
                  await SupabaseWorkspaceUtils.updateWorkspace(workspace.id, {
                    name: updatedWorkspaceData.name,
                    description: updatedWorkspaceData.description,
                    instructions: updatedWorkspaceData.instructions,
                    settings: updatedWorkspaceData.settings,
                    image_path: updatedWorkspaceData.image_path,
                  });

                setWorkspaces(
                  workspaces.map((w) =>
                    w.id === workspace.id ? updatedWorkspace : w
                  )
                );

                setIsSettingsOpen(false);
              } catch (error) {
                console.error("Error updating workspace:", error);
              }
            }}
            onDeleteWorkspace={async () => {
              try {
                if (workspaces.length <= 1) {
                  return;
                }

                if (
                  !confirm(
                    "Are you sure you want to delete this project? This action cannot be undone."
                  )
                ) {
                  return;
                }

                await SupabaseWorkspaceUtils.deleteWorkspace(workspace.id);

                setWorkspaces(workspaces.filter((w) => w.id !== workspace.id));

                setIsSettingsOpen(false);

                navigate("/chat");
              } catch (error) {
                console.error("Error deleting workspace:", error);
              }
            }}
          />

          {/* Edit Project Dialog */}
          <EditProjectDialog
            isOpen={isEditProjectOpen}
            onOpenChange={setIsEditProjectOpen}
            workspace={workspace}
            onSave={(updatedWorkspace) => {
              setWorkspaces(
                workspaces.map((w) =>
                  w.id === workspace.id ? { ...w, ...updatedWorkspace } : w
                )
              );
            }}
          />

          {/* Manage Team Dialog */}
          {workspace?.is_team && (
            <ManageTeamDialog
              isOpen={isManageTeamOpen}
              onOpenChange={setIsManageTeamOpen}
              workspace={workspace}
              initialTab={manageTeamInitialTab}
            />
          )}

          {/* Rename Project Dialog */}
          <ProjectRenameDialog
            isOpen={isRenameDialogOpen}
            onOpenChange={setIsRenameDialogOpen}
            workspaceToRename={workspace}
            newWorkspaceName={newWorkspaceName}
            setNewWorkspaceName={setNewWorkspaceName}
            isRenamingWorkspace={isRenamingWorkspace}
            onRename={handleWorkspaceRename}
            onCancel={() => setIsRenameDialogOpen(false)}
          />

          {/* Delete Project Dialog */}
          <ProjectDeleteDialog
            isOpen={isDeleteDialogOpen}
            onOpenChange={setIsDeleteDialogOpen}
            workspaceToDelete={workspace}
            isDeletingWorkspace={isDeletingWorkspace}
            onDelete={handleWorkspaceDelete}
            onCancel={() => setIsDeleteDialogOpen(false)}
          />
        </>
      )}
    </>
  );
}

export default ProjectsNavBar;
